/**
 * Zod Schemas for Search API
 * Ensures type safety and prevents drift between API implementation and tests
 */

import { z } from 'zod';

// Search request query parameters schema
export const SearchQueryParamsSchema = z.object({
  // Text search
  q: z.string().optional(),

  // Category filters
  categoryId: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val;
    return val.includes(',') ? val.split(',') : val;
  }),
  categoryGroupId: z.string().optional(),

  // Category exclusion filters
  excludeCategoryId: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val;
    return val.includes(',') ? val.split(',') : val;
  }),
  excludeCategoryGroupId: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val;
    return val.includes(',') ? val.split(',') : val;
  }),

  // Price filters
  minPrice: z.string().optional().transform((val) => val ? parseFloat(val) : undefined),
  maxPrice: z.string().optional().transform((val) => val ? parseFloat(val) : undefined),

  // Item condition filter (supports multiple conditions)
  itemCondition: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val;
    return val.split(',');
  }),

  // Boolean filters
  hasPhoto: z.union([
    z.string(),
    z.boolean()
  ]).optional().transform((val) => {
    if (val === undefined) return undefined;
    if (typeof val === 'boolean') return val;
    return val === 'true' || val === '1';
  }),
  shippable: z.union([
    z.string(),
    z.boolean()
  ]).optional().transform((val) => {
    if (val === undefined) return undefined;
    if (typeof val === 'boolean') return val;
    return val === 'true' || val === '1';
  }),

  // Geographic filters
  latitude: z.string().optional().transform((val) => {
    if (!val) return undefined;
    const num = parseFloat(val);
    if (num < -90 || num > 90) throw new Error('Latitude must be between -90 and 90');
    return num;
  }),
  longitude: z.string().optional().transform((val) => {
    if (!val) return undefined;
    const num = parseFloat(val);
    if (num < -180 || num > 180) throw new Error('Longitude must be between -180 and 180');
    return num;
  }),
  distance: z.string().optional().transform((val) => val ? parseFloat(val) : undefined),
  distanceStart: z.string().optional().transform((val) => val ? parseFloat(val) : undefined),

  // Membership filter
  membershipType: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val.map(v => parseInt(v, 10));
    if (val.includes(',')) return val.split(',').map(v => parseInt(v, 10));
    return parseInt(val, 10);
  }),

  // Featured filter (for partner-featured listings)
  featured: z.union([
    z.string(),
    z.boolean()
  ]).optional().transform((val) => {
    if (val === undefined) return undefined;
    if (typeof val === 'boolean') return val;
    return val === 'true' || val === '1';
  }),

  // ID filters
  userId: z.string().optional().transform((val) => val ? parseInt(val, 10) : undefined),
  excludeUserId: z.string().optional().transform((val) => val ? parseInt(val, 10) : undefined),
  dataSourceId: z.string().optional().transform((val) => val ? parseInt(val, 10) : undefined),
  status: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;

    // Valid status values: 1=Active, 4=Pending, 5=Sold
    const validStatuses = [1, 4, 5];

    // Handle array input
    if (Array.isArray(val)) {
      const parsed = val
        .map(v => parseInt(v, 10))
        .filter(v => !isNaN(v) && validStatuses.includes(v));
      return parsed.length > 0 ? parsed : undefined;
    }

    // Handle comma-separated string
    if (val.includes(',')) {
      const parsed = val
        .split(',')
        .map(v => parseInt(v.trim(), 10))
        .filter(v => !isNaN(v) && validStatuses.includes(v));
      return parsed.length > 0 ? parsed : undefined;
    }

    // Handle single value
    const parsed = parseInt(val, 10);
    return !isNaN(parsed) && validStatuses.includes(parsed) ? parsed : undefined;
  }),

  // Tags filter
  tags: z.union([
    z.string(),
    z.array(z.string())
  ]).optional().transform((val) => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val;
    return val.split(',');
  }),

  // Pagination
  page: z.string().optional().default('1').transform((val) => parseInt(val, 10)),
  limit: z.string().optional().default('32').transform((val) => parseInt(val, 10)),
});

export type SearchQueryParams = z.infer<typeof SearchQueryParamsSchema>;

// User photo object - matches format from getUserPhotoUrls
export const UserPhotoSchema = z.object({
  url: z.string(),
  thumbUrl: z.string(),
}).nullable();

// User object in listing response
export const ListingUserSchema = z.object({
  id: z.union([z.string(), z.number()]),
  firstName: z.string(),
  lastName: z.string(),
  trustLevel: z.number(),
  photos: UserPhotoSchema,
});

export type ListingUser = z.infer<typeof ListingUserSchema>;

// Photo object in listing response
export const ListingPhotoSchema = z.object({
  url: z.string(),
  thumbUrl: z.string(),
  '320x320Url': z.string(),
});

export type ListingPhoto = z.infer<typeof ListingPhotoSchema>;

// Listing object in search response
export const SearchListingSchema = z.object({
  id: z.string(),
  title: z.string(),
  text: z.string().nullish(), // Can be null or undefined
  price: z.number(),
  cost: z.number(),
  original_price: z.number().nullish(),
  slashedPrice: z.string().nullish(),
  percentOff: z.number().nullish(),
  categoryId: z.number(),
  categoryGroupId: z.number(),
  categoryName: z.string().optional(),
  itemCondition: z.number().nullish(),
  status: z.number(),
  shippable: z.union([z.number(), z.boolean()]),
  locationText: z.string().nullish(), // Can be null or undefined
  createdDate: z.string().nullish(), // Can be null or undefined
  featuredDate: z.string().nullish(), // Can be null or undefined
  tags: z.array(z.string()).optional().default([]),
  dataSourceId: z.number().nullish(),
  remote_url: z.string().nullish(),
  height: z.number().nullish(),
  width: z.number().nullish(),
  depth: z.number().nullish(),
  weight: z.number().nullish(),
  vin: z.string().nullish(),
  mileage: z.number().nullish(),
  hasPhoto: z.number(),
  photo: z.string().nullish(),
  photos: z.array(ListingPhotoSchema),
  user: ListingUserSchema.nullish(),
  distance: z.number().optional(),
  /** This seller's live listings of the same item, this one included (utils/listing-copies.ts). */
  copies: z.number().int().positive().optional(),
});

export type SearchListing = z.infer<typeof SearchListingSchema>;

// Search API success response
export const SearchSuccessResponseSchema = z.object({
  success: z.literal(true),
  listings: z.array(SearchListingSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  hasMore: z.boolean(),
});

export type SearchSuccessResponse = z.infer<typeof SearchSuccessResponseSchema>;

// Search API error response
export const SearchErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

export type SearchErrorResponse = z.infer<typeof SearchErrorResponseSchema>;

// Search API response (union of success and error)
export const SearchResponseSchema = z.union([
  SearchSuccessResponseSchema,
  SearchErrorResponseSchema,
]);

export type SearchResponse = z.infer<typeof SearchResponseSchema>;

// Helper function to validate search response
export function validateSearchResponse(data: unknown): SearchResponse {
  return SearchResponseSchema.parse(data);
}

// Helper function to validate search query params
export function validateSearchQueryParams(data: unknown): SearchQueryParams {
  return SearchQueryParamsSchema.parse(data);
}

// =============================================================================
// HOME LISTINGS API SCHEMAS
// =============================================================================

export const HomeListingsRequestSchema = z.object({
  q: z.string().optional(),
  page: z.string().optional().default('1'),
  limit: z.string().optional().default('32'),
  minPrice: z.string().optional(),
  maxPrice: z.string().optional(),
  itemCondition: z.string().optional(),
  shippable: z.string().optional(),
  status: z.string().optional().default('1'),
});

export type HomeListingsRequest = z.infer<typeof HomeListingsRequestSchema>;

export const HomeListingsResponseSchema = z.union([
  SearchSuccessResponseSchema,
  SearchErrorResponseSchema,
]);

export type HomeListingsResponse = z.infer<typeof HomeListingsResponseSchema>;

// =============================================================================
// HOME SHOWCASES API SCHEMAS
// =============================================================================

export const HomeShowcasesRequestSchema = z.object({
  minProductsPerGroup: z.string().optional().default('7'),
  categoryGroups: z.string().optional().default('12,15,26,34,23,19,24,17,25,35,20'),
});

export type HomeShowcasesRequest = z.infer<typeof HomeShowcasesRequestSchema>;

// Category group showcase item
export const CategoryGroupShowcaseSchema = z.object({
  categoryGroupId: z.number(),
  categoryGroupName: z.string(),
  categoryGroupSlug: z.string(),
  listings: z.array(SearchListingSchema),
});

export type CategoryGroupShowcase = z.infer<typeof CategoryGroupShowcaseSchema>;

export const HomeShowcasesSuccessResponseSchema = z.object({
  success: z.literal(true),
  showcases: z.array(CategoryGroupShowcaseSchema),
  total: z.number(),
});

export type HomeShowcasesSuccessResponse = z.infer<typeof HomeShowcasesSuccessResponseSchema>;

export const HomeShowcasesResponseSchema = z.union([
  HomeShowcasesSuccessResponseSchema,
  SearchErrorResponseSchema,
]);

export type HomeShowcasesResponse = z.infer<typeof HomeShowcasesResponseSchema>;

// =============================================================================
// SHOWCASE API SCHEMAS
// =============================================================================

export const ShowcaseRequestSchema = z.object({
  categoryGroupId: z.string(),
  minProductsPerCategory: z.string().optional().default('7'),
  q: z.string().optional(),
  minPrice: z.string().optional(),
  maxPrice: z.string().optional(),
  itemCondition: z.string().optional(),
  hasPhoto: z.string().optional(),
  categories: z.string().optional(),
});

export type ShowcaseRequest = z.infer<typeof ShowcaseRequestSchema>;

// Showcase group item
export const ShowcaseGroupSchema = z.object({
  categoryId: z.number(),
  categoryName: z.string(),
  categorySlug: z.string(),
  listings: z.array(SearchListingSchema),
});

export type ShowcaseGroup = z.infer<typeof ShowcaseGroupSchema>;

export const ShowcaseSuccessResponseSchema = z.object({
  success: z.literal(true),
  fullShowcases: z.array(ShowcaseGroupSchema),
  partialShowcases: z.array(ShowcaseGroupSchema),
  hasEnoughForShowcase: z.boolean(),
  total: z.number(),
});

export type ShowcaseSuccessResponse = z.infer<typeof ShowcaseSuccessResponseSchema>;

export const ShowcaseResponseSchema = z.union([
  ShowcaseSuccessResponseSchema,
  SearchErrorResponseSchema,
]);

export type ShowcaseResponse = z.infer<typeof ShowcaseResponseSchema>;

// =============================================================================
// TAG SEARCH API SCHEMAS
// =============================================================================

export const TagSearchRequestSchema = z.object({
  tag: z.string().optional(),
  tags: z.string().optional(),
  q: z.string().optional(),
  categoryId: z.string().optional(),
  categoryGroupId: z.string().optional(),
  minPrice: z.string().optional(),
  maxPrice: z.string().optional(),
  itemCondition: z.string().optional(),
  shippable: z.string().optional(),
  userId: z.string().optional(),
  dataSourceId: z.string().optional(),
  hasPhoto: z.string().optional(),
  page: z.string().optional().default('1'),
  limit: z.string().optional().default('20'),
  status: z.string().optional(),
});

export type TagSearchRequest = z.infer<typeof TagSearchRequestSchema>;

// Tag metadata
export const TagMetadataSchema = z.object({
  headings: z.array(z.string()),
  copy: z.array(z.string()),
  images: z.array(z.string()),
  count: z.number(),
  lastUsed: z.coerce.date(), // Use coerce to handle JSON string serialization
});

export type TagMetadata = z.infer<typeof TagMetadataSchema>;

// Tag search listing (slightly different from main search)
export const TagSearchListingSchema = z.object({
  id: z.union([z.string(), z.number()]),
  title: z.string(),
  text: z.string().nullish(),
  price: z.number(),
  originalPrice: z.number().optional(),
  cost: z.number(),
  categoryId: z.number(),
  categoryGroupId: z.number(),
  itemCondition: z.number().nullish(),
  status: z.number(),
  shippable: z.union([z.number(), z.boolean()]),
  locationText: z.string().nullish(),
  createdDate: z.string().nullish(),
  featuredDate: z.string().nullish(),
  tags: z.array(z.string()).optional(),
  height: z.number().nullish(),
  width: z.number().nullish(),
  depth: z.number().nullish(),
  weight: z.number().nullish(),
  _tagGroup: z.string().optional(),
  user: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    trustLevel: z.number(),
  }),
  photos: z.array(z.object({
    url: z.string(),
    thumbUrl: z.string().optional(),
    '320x320Url': z.string().optional(),
  })),
  distance: z.number(),
});

export type TagSearchListing = z.infer<typeof TagSearchListingSchema>;

export const TagSearchSuccessResponseSchema = z.object({
  success: z.literal(true),
  listings: z.array(TagSearchListingSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  hasMore: z.boolean(),
  tagMetadata: z.record(z.string(), TagMetadataSchema).optional(),
  groupCounts: z.record(z.string(), z.number()).optional(),
});

export type TagSearchSuccessResponse = z.infer<typeof TagSearchSuccessResponseSchema>;

export const TagSearchResponseSchema = z.union([
  TagSearchSuccessResponseSchema,
  SearchErrorResponseSchema,
]);

export type TagSearchResponse = z.infer<typeof TagSearchResponseSchema>;

// =============================================================================
// SIMILAR TAGS API SCHEMAS
// =============================================================================

export const SimilarTagsRequestSchema = z.object({
  tag: z.string(),
  limit: z.string().optional().default('10'),
  minScore: z.string().optional().default('0.5'),
  useOpenAI: z.string().optional().default('false'),
});

export type SimilarTagsRequest = z.infer<typeof SimilarTagsRequestSchema>;

export const SimilarTagSchema = z.object({
  tag: z.string(),
  score: z.number(),
  count: z.number(),
});

export type SimilarTag = z.infer<typeof SimilarTagSchema>;

export const SimilarTagsSuccessResponseSchema = z.object({
  success: z.literal(true),
  tag: z.string(),
  similarTags: z.array(SimilarTagSchema),
  method: z.enum(['elasticsearch', 'fallback']),
});

export type SimilarTagsSuccessResponse = z.infer<typeof SimilarTagsSuccessResponseSchema>;

export const SimilarTagsResponseSchema = z.union([
  SimilarTagsSuccessResponseSchema,
  SearchErrorResponseSchema,
]);

export type SimilarTagsResponse = z.infer<typeof SimilarTagsResponseSchema>;

// =============================================================================
// ALL TAGS API SCHEMAS
// =============================================================================

export const AllTagsRequestSchema = z.object({
  limit: z.string().optional(),
  categoryGroupId: z.string().optional(),
});

export type AllTagsRequest = z.infer<typeof AllTagsRequestSchema>;

export const ApprovedTagSchema = z.object({
  tag: z.string(),
  count: z.number(),
  lastUsed: z.coerce.date(), // Use coerce to handle JSON string serialization
  headings: z.string().nullish(),
  copy: z.string().nullish(),
  images: z.string().nullish(),
  categoryGroupIds: z.array(z.number()).default([]),
});

export type ApprovedTag = z.infer<typeof ApprovedTagSchema>;

export const AllTagsSuccessResponseSchema = z.object({
  success: z.literal(true),
  tags: z.array(ApprovedTagSchema),
  total: z.number(),
});

export type AllTagsSuccessResponse = z.infer<typeof AllTagsSuccessResponseSchema>;

export const AllTagsResponseSchema = z.union([
  AllTagsSuccessResponseSchema,
  SearchErrorResponseSchema,
]);

export type AllTagsResponse = z.infer<typeof AllTagsResponseSchema>;

// =============================================================================
// SEARCH BAR COMPONENT STATE SCHEMAS
// Internal search state used by the SearchBar component and SearchProvider
// =============================================================================

// Schema for search params state (component internal state — numeric values)
export const SearchBarParamsSchema = z.object({
  q: z.string().optional(),
  hideSoldPending: z.boolean().optional(),
  minPrice: z.number().optional(),
  maxPrice: z.number().optional(),
  itemCondition: z.union([z.number(), z.array(z.number())]).optional(),
  categoryGroupId: z.number().optional(),
  sold: z.boolean().optional(),
  shippable: z.boolean().optional(),
  page: z.number().optional(),
}).passthrough();

export type SearchBarParams = z.infer<typeof SearchBarParamsSchema>;

// Schema for recent search entries stored in SearchProvider
export const RecentSearchSchema = z.object({
  q: z.string(),
  page: z.number().optional(),
  minPrice: z.number().optional(),
  maxPrice: z.number().optional(),
  categoryGroupId: z.number().optional(),
  itemCondition: z.union([z.number(), z.array(z.number())]).optional(),
  sold: z.boolean().optional(),
  shippable: z.boolean().optional(),
}).passthrough();

export type RecentSearch = z.infer<typeof RecentSearchSchema>;
